import { useMemo, useEffect } from 'react'
import { generatePath } from 'react-router'
import { Block, BLOCKS, EntryLinkBlock, Inline, INLINES, MARKS } from '@contentful/rich-text-types'
import { merge } from 'lodash-es'

import { defaultRichTextPropOptions, warnAboutOptionIssues } from './utils'
import { routes } from '~/queries/routes'
import { EmbeddedEntry } from './EmbeddedEntry'

import type { RichTextElementProps, RichTextProps } from './types'
import type { Options as ContentfulRichTextOptions } from '@contentful/rich-text-react-renderer'
import { Link } from '@remix-run/react'

const EmbeddedEntryFunc = (node: Block | Inline, children: React.ReactNode) => (
  <EmbeddedEntry node={node as EntryLinkBlock}>{children}</EmbeddedEntry>
)

export const useRichText = ({ options }: Pick<RichTextProps, 'options'>) => {
  const elementProps: RichTextElementProps = merge(
    {},
    defaultRichTextPropOptions,
    options?.extraProps ?? {},
  )

  const baseOptions = useMemo<ContentfulRichTextOptions>(
    () => ({
      renderNode: {
        [BLOCKS.HEADING_1]: (_, children) => (
          <h1 {...elementProps[BLOCKS.HEADING_1]} className="text-2xl">
            {children}
          </h1>
        ),
        [BLOCKS.HEADING_2]: (node, children) => (
          <h2 {...elementProps[BLOCKS.HEADING_2]} className="text-xl" data-id={node.data.id}>
            {children}
          </h2>
        ),
        [BLOCKS.HEADING_3]: (node, children) => (
          <h3 {...elementProps[BLOCKS.HEADING_3]} className="text-l" data-id={node.data.id}>
            {children}
          </h3>
        ),
        [BLOCKS.HEADING_4]: (_, children) => (
          <h4 {...elementProps[BLOCKS.HEADING_4]} className="text-m">
            {children}
          </h4>
        ),
        [BLOCKS.HEADING_5]: (_, children) => (
          <h5 {...elementProps[BLOCKS.HEADING_5]} className="text-s">
            {children}
          </h5>
        ),
        [BLOCKS.HEADING_6]: (_, children) => (
          <h6 {...elementProps[BLOCKS.HEADING_6]} className="text-xs">
            {children}
          </h6>
        ),
        [BLOCKS.PARAGRAPH]: (_, children) => {
          // If there are empty lines, convert them to <br />
          if (
            Array.isArray(children) &&
            (children.length === 0 || (children.length === 1 && !children?.[0]))
          ) {
            return <br />
          }

          return (
            <p {...elementProps[BLOCKS.PARAGRAPH]} className="text-s">
              {children}
            </p>
          )
        },
        [BLOCKS.OL_LIST]: (_, children) => (
          <ol
            className="list-none p-0 rich-text__ol"
            style={{ counterReset: 'ol-counter' }}
            {...elementProps[BLOCKS.OL_LIST]}
          >
            {children}
          </ol>
        ),
        [BLOCKS.UL_LIST]: (_, children) => (
          <ul className="list-none p-0 rich-text__ul" {...elementProps[BLOCKS.UL_LIST]}>
            {children}
          </ul>
        ),
        [BLOCKS.LIST_ITEM]: (_, children) => (
          <li className="rich-text__li" {...elementProps[BLOCKS.LIST_ITEM]}>
            {children}
          </li>
        ),
        [BLOCKS.TABLE]: (node, children) => {
          return (
            <div className="w-full overflow-auto my-0 -m-4 mt-2 mb-0 max-lg:mt-8 max-md:mt-7 [&:not(:last-child)]:mb-10 max-xl:[&:not(:last-child)]:mb-8">
              <p {...elementProps[BLOCKS.PARAGRAPH]}>
                <table className="rich-text__table" {...elementProps[BLOCKS.TABLE]}>
                  <table style={{ width: '100%' }}>{children}</table>
                </table>
              </p>
            </div>
          )
        },

        [BLOCKS.EMBEDDED_ENTRY]: EmbeddedEntryFunc,
        [BLOCKS.EMBEDDED_ASSET]: EmbeddedEntryFunc, // create a component just for the asset
        [INLINES.HYPERLINK]: (node, children) => {
          const href = node.data.uri

          if (href.includes('player.vimeo.com/video')) {
            return (
              <div className="relative h-0 pb-[56.25%]">
                <iframe
                  className="absolute top-0 left-0 w-full h-full border-none"
                  title={children?.toString()}
                  src={href}
                  width="560"
                  height="315"
                  allowFullScreen
                />
              </div>
            )
          } else if (href.includes('youtube.com/embed') || href.includes('youtube.com/watch')) {
            return (
              <div className="relative h-0 pb-[56.25%]">
                <iframe
                  className="absolute top-0 left-0 w-full h-full border-none"
                  title={children?.toString()}
                  src={href.replace('/watch?v=', '/embed/')}
                  width="560"
                  height="315"
                  allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )
          } else {
            return (
              <a
                className="text-blue-50 underline hover:text-blue-60"
                href={href}
                target="_blank"
                rel="noreferrer"
                {...elementProps[INLINES.ASSET_HYPERLINK]}
              >
                {children}
              </a>
            )
          }
        },
        //TODO: Fix ASSET_HYPERLINK
        [INLINES.ASSET_HYPERLINK]: (node, children) => {
          return (
            <Link
              className="text-blue-50 underline hover:text-blue-60"
              to={node.data.uri}
              {...elementProps[INLINES.ASSET_HYPERLINK]}
            >
              {children}
            </Link>
          )
        },
        [INLINES.ENTRY_HYPERLINK]: (node, children) => {
          const { fields, sys } = node?.data?.target || {}
          const { id, contentType } = sys || {}
          const contentTypeId = contentType?.sys?.id

          if (!id || !fields?.slug) {
            return children
          }

          const route: string | undefined = (routes as Record<string, string>)[contentTypeId]

          return (
            <Link
              className="text-blue-50 underline hover:text-blue-60"
              to={generatePath(route || '', { slug: fields.slug })}
              {...elementProps[INLINES.ENTRY_HYPERLINK]}
            >
              {children}
            </Link>
          )
        },
        [INLINES.EMBEDDED_ENTRY]: EmbeddedEntryFunc,
      },
      renderMark: {
        [MARKS.BOLD]: (text) => <strong>{text}</strong>,
        [MARKS.ITALIC]: (text) => <em>{text}</em>,
      },
      stripEmptyTrailingParagraph: true,
    }),

    [elementProps],
  )

  // Check to see if the developer is confused about how the options work.
  useEffect(() => {
    if (options) {
      warnAboutOptionIssues(options)
    }
  }, [options])

  const renderOptions = merge({}, baseOptions, options)

  return {
    renderOptions,
  }
}
